import React, { useState, useEffect, useCallback } from 'react';
import { Settings, BarChart3, Moon, Sun, Maximize, Minimize, RotateCcw, Copy, Download, Upload, Shuffle, Shield, Info, Grid3X3, Key, Hash } from 'lucide-react';
import { Helmet } from 'react-helmet-async';

interface CipherStats {
  totalEncryptions: number;
  totalDecryptions: number;
  totalOperations: number;
  totalCharactersProcessed: number;
  averageTextLength: number;
  longestText: number;
  coordinatePairsProcessed: number;
  totalGridsUsed: number;
  uniqueKeywords: Set<string>;
  averageKeywordLength: number;
}

interface CipherHistory {
  id: string;
  input: string;
  output: string;
  keyword: string;
  operation: 'encrypt' | 'decrypt';
  timestamp: Date;
  textLength: number;
  coordinatePairs: number;
}

const PolybiusSquareCipherApp: React.FC = () => {
  // Core state
  const [inputText, setInputText] = useState<string>('');
  const [outputText, setOutputText] = useState<string>('');
  const [keyword, setKeyword] = useState<string>('POLYBIUS');
  const [operation, setOperation] = useState<'encrypt' | 'decrypt'>('encrypt');
  
  // UI state
  const [isDarkMode, setIsDarkMode] = useState<boolean>(false);
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);
  const [activeModal, setActiveModal] = useState<'settings' | 'analytics' | 'help' | 'grid' | null>(null);
  
  // Advanced options state
  const [preserveCase, setPreserveCase] = useState<boolean>(true);
  const [preserveSpaces, setPreserveSpaces] = useState<boolean>(false);
  const [preservePunctuation, setPreservePunctuation] = useState<boolean>(false);
  const [showGrid, setShowGrid] = useState<boolean>(false);
  const [groupOutput, setGroupOutput] = useState<boolean>(true);
  const [groupSize, setGroupSize] = useState<number>(2);
  const [separator, setSeparator] = useState<string>(' ');
  const [jReplacesI, setJReplacesI] = useState<boolean>(true);
  const [useKeyword, setUseKeyword] = useState<boolean>(true);
  const [coordinateStyle, setCoordinateStyle] = useState<'numeric' | 'alphabetic'>('numeric');
  
  // Analytics state
  const [stats, setStats] = useState<CipherStats>({
    totalEncryptions: 0,
    totalDecryptions: 0,
    totalOperations: 0,
    totalCharactersProcessed: 0,
    averageTextLength: 0,
    longestText: 0,
    coordinatePairsProcessed: 0,
    totalGridsUsed: 0,
    uniqueKeywords: new Set<string>(),
    averageKeywordLength: 0
  });
  const [history, setHistory] = useState<CipherHistory[]>([]);
  const [polybiusGrid, setPolybiusGrid] = useState<string[][]>([]);

  // Generate 5x5 grid from keyword
  const generateGrid = useCallback((keywordStr: string): string[][] => {
    const cleanKeyword = keywordStr.replace(/[^A-Z]/g, '').toUpperCase();
    const alphabet = jReplacesI ? 'ABCDEFGHIKLMNOPQRSTUVWXYZ' : 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    let keyString = '';
    const used = new Set<string>();
    
    if (useKeyword) {
      // Add unique letters from keyword
      for (const char of cleanKeyword) {
        if (!used.has(char) && (char !== 'J' || !jReplacesI)) {
          keyString += char;
          used.add(char);
        }
      }
    }
    
    // Add remaining letters
    for (const char of alphabet) {
      if (!used.has(char) && (char !== 'J' || !jReplacesI)) {
        keyString += char;
        used.add(char);
      }
    }
    
    // Create 5x5 grid
    const grid: string[][] = [];
    for (let i = 0; i < 5; i++) {
      grid[i] = [];
      for (let j = 0; j < 5; j++) {
        grid[i][j] = keyString[i * 5 + j] || '';
      }
    }
    
    return grid;
  }, [jReplacesI, useKeyword]);

  // Find position of letter in grid
  const findPosition = useCallback((letter: string, grid: string[][]): [number, number] => {
    const searchLetter = jReplacesI && letter === 'J' ? 'I' : letter;
    for (let i = 0; i < 5; i++) {
      for (let j = 0; j < 5; j++) {
        if (grid[i][j] === searchLetter) {
          return [i, j];
        }
      }
    }
    return [-1, -1];
  }, [jReplacesI]);

  // Convert coordinates to string
  const coordinatesToString = useCallback((row: number, col: number): string => {
    if (coordinateStyle === 'numeric') {
      return `${row + 1}${col + 1}`;
    } else {
      const alphabet = 'ABCDE';
      return `${alphabet[row]}${alphabet[col]}`;
    }
  }, [coordinateStyle]);

  // Convert string to coordinates
  const stringToCoordinates = useCallback((coordStr: string): [number, number] => {
    if (coordinateStyle === 'numeric') {
      if (coordStr.length === 2) {
        const row = parseInt(coordStr[0]) - 1;
        const col = parseInt(coordStr[1]) - 1;
        if (row >= 0 && row < 5 && col >= 0 && col < 5) {
          return [row, col];
        }
      }
    } else {
      if (coordStr.length === 2) {
        const alphabet = 'ABCDE';
        const row = alphabet.indexOf(coordStr[0].toUpperCase());
        const col = alphabet.indexOf(coordStr[1].toUpperCase());
        if (row >= 0 && row < 5 && col >= 0 && col < 5) {
          return [row, col];
        }
      }
    }
    return [-1, -1];
  }, [coordinateStyle]);

  // Clean text based on preservation settings
  const cleanText = useCallback((text: string): string => {
    let cleaned = text;
    
    if (!preserveSpaces) {
      cleaned = cleaned.replace(/\s/g, '');
    }
    
    if (!preservePunctuation) {
      cleaned = cleaned.replace(/[^\w\s]/g, '');
    }
    
    if (!preserveCase) {
      cleaned = cleaned.toUpperCase();
    }
    
    return cleaned;
  }, [preserveSpaces, preservePunctuation, preserveCase]);

  // Prepare text for Polybius Square cipher
  const prepareText = useCallback((text: string): string => {
    let prepared = text.replace(/[^A-Z]/g, '').toUpperCase();
    if (jReplacesI) {
      prepared = prepared.replace(/J/g, 'I');
    }
    return prepared;
  }, [jReplacesI]);

  // Polybius Square encryption
  const polybiusEncrypt = useCallback((text: string, keywordStr: string): string => {
    if (!text) return '';
    
    const grid = generateGrid(keywordStr);
    setPolybiusGrid(grid);
    
    const preparedText = prepareText(text);
    let result = '';
    
    // Process each character
    for (const char of preparedText) {
      const [row, col] = findPosition(char, grid);
      
      if (row === -1 || col === -1) continue;
      
      result += coordinatesToString(row, col);
    }
    
    // Format output
    if (groupOutput && result) {
      const groups = [];
      for (let i = 0; i < result.length; i += groupSize) {
        groups.push(result.slice(i, i + groupSize));
      }
      result = groups.join(separator);
    }
    
    return result;
  }, [generateGrid, prepareText, findPosition, coordinatesToString, groupOutput, groupSize, separator]);

  // Polybius Square decryption
  const polybiusDecrypt = useCallback((text: string, keywordStr: string): string => {
    if (!text) return '';
    
    const grid = generateGrid(keywordStr);
    setPolybiusGrid(grid);
    
    // Remove separators and grouping
    const cleanedText = text.replace(new RegExp(`\\${separator}`, 'g'), '').replace(/\s/g, '');
    let result = '';
    
    // Process coordinate pairs
    for (let i = 0; i < cleanedText.length; i += 2) {
      const coordStr = cleanedText.slice(i, i + 2);
      const [row, col] = stringToCoordinates(coordStr);
      
      if (row === -1 || col === -1) continue;
      
      result += grid[row][col];
    }
    
    return result;
  }, [generateGrid, stringToCoordinates, separator]);

  // Process text - main cipher logic
  const processText = useCallback(() => {
    const keywordToUse = useKeyword ? keyword.trim() : '';
    
    const result = operation === 'encrypt' 
      ? polybiusEncrypt(inputText, keywordToUse)
      : polybiusDecrypt(inputText, keywordToUse);
    
    setOutputText(result);
    return result;
  }, [inputText, keyword, operation, polybiusEncrypt, polybiusDecrypt, useKeyword]);

  // Update analytics
  const updateAnalytics = useCallback((result: string) => {
    if (!inputText || !result) return;
    
    const cleanInput = inputText.replace(/[^A-Z]/g, '').toUpperCase();
    const coordinatePairs = operation === 'encrypt' ? cleanInput.length : Math.floor(inputText.replace(/[^0-9A-E]/g, '').length / 2);
    
    const newHistoryItem: CipherHistory = {
      id: Date.now().toString(),
      input: inputText,
      output: result,
      keyword: keyword,
      operation,
      timestamp: new Date(),
      textLength: inputText.length,
      coordinatePairs: coordinatePairs
    };
    
    setHistory(prev => [newHistoryItem, ...prev.slice(0, 49)]);
    
    setStats(prevStats => {
      const newUniqueKeywords = new Set(Array.from(prevStats.uniqueKeywords).concat([keyword.toUpperCase()]));
      const totalOps = prevStats.totalOperations + 1;
      const totalChars = prevStats.totalCharactersProcessed + inputText.length;
      
      return {
        ...prevStats,
        totalEncryptions: prevStats.totalEncryptions + (operation === 'encrypt' ? 1 : 0),
        totalDecryptions: prevStats.totalDecryptions + (operation === 'decrypt' ? 1 : 0),
        totalOperations: totalOps,
        totalCharactersProcessed: totalChars,
        averageTextLength: Math.round(totalChars / totalOps),
        longestText: Math.max(prevStats.longestText, inputText.length),
        coordinatePairsProcessed: prevStats.coordinatePairsProcessed + coordinatePairs,
        totalGridsUsed: prevStats.totalGridsUsed + 1,
        uniqueKeywords: newUniqueKeywords,
        averageKeywordLength: Math.round(Array.from(newUniqueKeywords).reduce((sum, k) => sum + k.length, 0) / newUniqueKeywords.size)
      };
    });
  }, [inputText, keyword, operation]);

  // Auto-process when inputs change
  useEffect(() => {
    if (inputText || (!useKeyword || keyword)) {
      const result = processText();
      if (result) {
        updateAnalytics(result);
      }
    } else {
      setOutputText('');
    }
  }, [inputText, keyword, operation, useKeyword]);

  // Update grid when keyword changes
  useEffect(() => {
    setPolybiusGrid(generateGrid(useKeyword ? keyword : ''));
  }, [keyword, generateGrid, useKeyword]);

  // Utility functions
  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch (err) {
      console.error('Failed to copy text');
    }
  };

  const downloadText = (text: string, filename: string) => {
    const blob = new Blob([text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const content = e.target?.result as string;
        setInputText(content);
      };
      reader.readAsText(file);
    }
  };

  const resetAll = () => {
    setInputText('');
    setOutputText('');
    setKeyword('POLYBIUS');
    setOperation('encrypt');
  };

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  const swapTexts = () => {
    const temp = inputText;
    setInputText(outputText.replace(/[^a-zA-Z0-9]/g, ''));
    setOutputText(temp);
    setOperation(operation === 'encrypt' ? 'decrypt' : 'encrypt');
  };

  const generateSampleText = () => {
    const samples = [
      "ATTACK AT DAWN",
      "THE POLYBIUS SQUARE IS ANCIENT",
      "MEET ME AT THE LIBRARY",
      "COORDINATES REVEAL THE MESSAGE",
      "CLASSICAL CRYPTOGRAPHY WORKS",
      "GRID CIPHER SYSTEM",
      "ANCIENT GREEK ENCRYPTION"
    ];
    const randomSample = samples[Math.floor(Math.random() * samples.length)];
    setInputText(randomSample);
  };

  const generateRandomKeyword = () => {
    const words = [
      'POLYBIUS', 'SQUARE', 'CIPHER', 'SECRET', 'CRYPTO', 'GRID',
      'ANCIENT', 'GREEK', 'ENCODE', 'DECODE', 'HIDDEN', 'MATRIX',
      'COORD', 'SYSTEM', 'CLASSICAL', 'HISTORY'
    ];
    const randomWord = words[Math.floor(Math.random() * words.length)];
    setKeyword(randomWord);
  };

  return (
    <>
      <Helmet>
        {/* Basic SEO */}
        <title>Polybius Square Cipher - Classical Grid Coordinate Encryption Tool | Free Online Cryptography</title>
        <meta name="description" content="Professional Polybius Square cipher tool with grid coordinates, keyword squares, and ancient Greek cryptography features. Perfect for classical cipher education and historical encryption." />
        <meta name="keywords" content="polybius square cipher, grid cipher, coordinate cipher, classical cryptography, ancient greek cipher, grid encryption, coordinate encryption, historical cipher, classical substitution, cryptography education" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Polybius Square Cipher - Classical Grid Encryption Tool" />
        <meta property="og:description" content="Professional Polybius Square cipher with grid coordinates, keyword squares, and ancient Greek cryptography features." />
        <meta property="og:image" content="/api/placeholder/1200/630" />
        
        {/* Additional Meta Tags */}
        <meta name="author" content="Polybius Square Cipher Tool" />
        <meta name="robots" content="index, follow" />
        <meta name="language" content="English" />
        
        {/* Theme Color */}
        <meta name="theme-color" content={isDarkMode ? '#1f2937' : '#ffffff'} />
        
        {/* Structured Data - JSON-LD */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebApplication",
            "name": "Polybius Square Cipher Tool",
            "alternateName": ["Grid Cipher", "Coordinate Cipher", "Ancient Greek Cipher"],
            "description": "Advanced Polybius Square cipher tool for grid coordinate encryption with keyword squares and classical cipher features.",
            "applicationCategory": ["SecurityApplication", "EducationalApplication", "HistoricalApplication"],
            "operatingSystem": "Any",
            "browserRequireries": "HTML5, JavaScript",
            "offers": {
              "@type": "Offer",
              "price": "0",
              "priceCurrency": "USD"
            },
            "featureList": [
              "Real-time Polybius Square encryption and decryption",
              "Grid coordinate visualization",
              "Keyword-based grid generation",
              "Classical cipher implementation",
              "Coordinate style options",
              "Ancient Greek historical features",
              "Analytics and usage statistics",
              "Educational cipher information"
            ]
          })}
        </script>
      </Helmet>

      <div className={`min-h-screen transition-colors duration-300 ${
        isDarkMode ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'
      } ${isFullscreen ? 'fixed inset-0 z-50' : ''}`}>
        
        {/* Header */}
        <header className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} border-b px-6 py-4`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Hash className="text-indigo-500" size={24} />
              <div>
                <h1 className="text-2xl font-bold">Polybius Square Cipher</h1>
                <p className="text-sm opacity-60">Classical Grid Coordinate Encryption (Ancient Greece)</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-2">
              {/* Grid Viewer */}
              <button
                onClick={() => setActiveModal(activeModal === 'grid' ? null : 'grid')}
                className={`p-2 rounded-lg transition-colors ${
                  isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                }`}
                title="View Polybius Grid"
              >
                <Grid3X3 size={20} />
              </button>
              
              {/* Help */}
              <button
                onClick={() => setActiveModal(activeModal === 'help' ? null : 'help')}
                className={`p-2 rounded-lg transition-colors ${
                  isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                }`}
                title="Help & Information"
              >
                <Info size={20} />
              </button>
              
              {/* Advanced Options */}
              <button
                onClick={() => setActiveModal(activeModal === 'settings' ? null : 'settings')}
                className={`p-2 rounded-lg transition-colors ${
                  isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                }`}
                title="Advanced Options"
              >
                <Settings size={20} />
              </button>
              
              {/* Analytics */}
              <button
                onClick={() => setActiveModal(activeModal === 'analytics' ? null : 'analytics')}
                className={`p-2 rounded-lg transition-colors ${
                  isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                }`}
                title="Analytics"
              >
                <BarChart3 size={20} />
              </button>
              
              {/* Theme Toggle */}
              <button
                onClick={() => setIsDarkMode(!isDarkMode)}
                className={`p-2 rounded-lg transition-colors ${
                  isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                }`}
                title="Toggle Theme"
              >
                {isDarkMode ? <Sun size={20} /> : <Moon size={20} />}
              </button>
              
              {/* Fullscreen Toggle */}
              <button
                onClick={toggleFullscreen}
                className={`p-2 rounded-lg transition-colors ${
                  isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                }`}
                title="Toggle Fullscreen"
              >
                {isFullscreen ? <Minimize size={20} /> : <Maximize size={20} />}
              </button>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="flex-1 p-6">
          <div className="max-w-6xl mx-auto space-y-6">
            
            {/* Controls */}
            <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg p-6 shadow-lg`}>
              <div className="grid grid-cols-1 md:grid-cols-6 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Operation</label>
                  <select
                    value={operation}
                    onChange={(e) => setOperation(e.target.value as 'encrypt' | 'decrypt')}
                    className={`w-full p-2 rounded border ${
                      isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'
                    }`}
                  >
                    <option value="encrypt">🔢 Encrypt</option>
                    <option value="decrypt">🔤 Decrypt</option>
                  </select>
                </div>
                
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-medium">Keyword</label>
                    <button
                      onClick={generateRandomKeyword}
                      className={`text-xs px-2 py-1 rounded ${
                        isDarkMode ? 'bg-indigo-600 hover:bg-indigo-700' : 'bg-indigo-500 hover:bg-indigo-600'
                      } text-white transition-colors`}
                      title="Generate Random Keyword"
                    >
                      Random
                    </button>
                  </div>
                  <input
                    type="text"
                    value={keyword}
                    onChange={(e) => setKeyword(e.target.value.replace(/[^a-zA-Z]/g, '').toUpperCase())}
                    placeholder="Enter keyword..."
                    disabled={!useKeyword}
                    className={`w-full p-2 rounded border ${
                      isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'
                    } ${!useKeyword ? 'opacity-50' : ''}`}
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-2">Coordinate Style</label>
                  <select
                    value={coordinateStyle}
                    onChange={(e) => setCoordinateStyle(e.target.value as 'numeric' | 'alphabetic')}
                    className={`w-full p-2 rounded border ${
                      isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'
                    }`}
                  >
                    <option value="numeric">Numeric (11-55)</option>
                    <option value="alphabetic">Alphabetic (AA-EE)</option>
                  </select>
                </div>
                
                <div className="flex items-end">
                  <button
                    onClick={swapTexts}
                    className={`w-full p-2 rounded border flex items-center justify-center space-x-2 ${
                      isDarkMode ? 'bg-indigo-600 border-indigo-500 hover:bg-indigo-700 text-white' : 'bg-indigo-500 border-indigo-400 hover:bg-indigo-600 text-white'
                    } transition-colors`}
                    title="Swap input and output"
                  >
                    <Shuffle size={16} />
                    <span>Swap</span>
                  </button>
                </div>
                
                <div className="flex items-end">
                  <button
                    onClick={generateSampleText}
                    className={`w-full p-2 rounded border flex items-center justify-center space-x-2 ${
                      isDarkMode ? 'bg-green-600 border-green-500 hover:bg-green-700 text-white' : 'bg-green-500 border-green-400 hover:bg-green-600 text-white'
                    } transition-colors`}
                    title="Generate sample text"
                  >
                    <Key size={16} />
                    <span>Sample</span>
                  </button>
                </div>
                
                <div className="flex items-end">
                  <button
                    onClick={resetAll}
                    className={`w-full p-2 rounded border flex items-center justify-center space-x-2 ${
                      isDarkMode ? 'bg-gray-700 border-gray-600 hover:bg-gray-600' : 'bg-gray-100 border-gray-300 hover:bg-gray-200'
                    } transition-colors`}
                  >
                    <RotateCcw size={16} />
                    <span>Reset</span>
                  </button>
                </div>
              </div>
              
              {/* Grid Preview */}
              {showGrid && polybiusGrid.length > 0 && (
                <div className={`p-4 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'} mb-4`}>
                  <h4 className="text-sm font-medium mb-3">Polybius Square Grid:</h4>
                  <div className="flex justify-center">
                    <div className="grid grid-cols-6 gap-1 w-fit">
                      {/* Headers */}
                      <div className="w-8 h-8"></div>
                      {[1, 2, 3, 4, 5].map(col => (
                        <div key={col} className={`w-8 h-8 flex items-center justify-center text-xs font-bold ${
                          isDarkMode ? 'text-indigo-400' : 'text-indigo-600'
                        }`}>
                          {coordinateStyle === 'numeric' ? col : String.fromCharCode(64 + col)}
                        </div>
                      ))}
                      
                      {/* Grid with letters */}
                      {polybiusGrid.map((row, rowIndex) => (
                        <React.Fragment key={rowIndex}>
                          <div className={`w-8 h-8 flex items-center justify-center text-xs font-bold ${
                            isDarkMode ? 'text-indigo-400' : 'text-indigo-600'
                          }`}>
                            {coordinateStyle === 'numeric' ? rowIndex + 1 : String.fromCharCode(65 + rowIndex)}
                          </div>
                          {row.map((letter, colIndex) => (
                            <div
                              key={`${rowIndex}-${colIndex}`}
                              className={`w-8 h-8 flex items-center justify-center text-xs font-mono font-bold border ${
                                isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300 text-gray-900'
                              }`}
                            >
                              {letter}
                            </div>
                          ))}
                        </React.Fragment>
                      ))}
                    </div>
                  </div>
                  <div className="text-xs opacity-60 mt-3 text-center">
                    {useKeyword ? `Using keyword: ${keyword}` : 'Using standard alphabet'} • 
                    Each letter is encoded as its row-column coordinates
                  </div>
                </div>
              )}
            </div>

            {/* Text Areas */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Input */}
              <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg p-6 shadow-lg`}>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold">Input Text</h3>
                  <div className="text-sm opacity-60">
                    {inputText.length} characters
                  </div>
                </div>
                <textarea
                  value={inputText}
                  onChange={(e) => setInputText(e.target.value)}
                  placeholder="Enter text to encrypt or decrypt using Polybius Square cipher..."
                  className={`w-full h-64 p-4 rounded border resize-none ${
                    isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-300'
                  }`}
                />
                <div className="mt-2 flex items-center space-x-4">
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="file"
                      accept=".txt"
                      onChange={handleFileUpload}
                      className="sr-only"
                    />
                    <Upload size={16} className="mr-1" />
                    <span className="text-sm">Upload File</span>
                  </label>
                </div>
              </div>

              {/* Output */}
              <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg p-6 shadow-lg`}>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold">Output Text</h3>
                  <div className="flex items-center space-x-2">
                    <div className="text-sm opacity-60">
                      {outputText.length} characters
                    </div>
                    <button
                      onClick={() => copyToClipboard(outputText)}
                      className={`p-2 rounded ${
                        isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                      } transition-colors`}
                      title="Copy to clipboard"
                    >
                      <Copy size={16} />
                    </button>
                    <button
                      onClick={() => downloadText(outputText, 'polybius-output.txt')}
                      className={`p-2 rounded ${
                        isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                      } transition-colors`}
                      title="Download as file"
                    >
                      <Download size={16} />
                    </button>
                  </div>
                </div>
                <textarea
                  value={outputText}
                  readOnly
                  className={`w-full h-64 p-4 rounded border resize-none ${
                    isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-300'
                  }`}
                />
              </div>
            </div>
          </div>
        </main>

        {/* Grid Modal */}
        {activeModal === 'grid' && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg p-6 max-w-3xl w-full max-h-[80vh] overflow-y-auto`}>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold flex items-center space-x-2">
                  <Grid3X3 className="text-indigo-500" />
                  <span>Polybius Square Grid</span>
                </h2>
                <button
                  onClick={() => setActiveModal(null)}
                  className={`p-2 rounded ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
                >
                  ×
                </button>
              </div>
              
              <div className="space-y-6">
                {polybiusGrid.length > 0 && (
                  <>
                    <div className="text-center">
                      <div className="text-sm opacity-60 mb-4">
                        {useKeyword ? `Keyword: ${keyword}` : 'Standard Alphabet'} • 
                        Style: {coordinateStyle === 'numeric' ? 'Numeric (1-5)' : 'Alphabetic (A-E)'}
                      </div>
                    </div>
                    
                    {/* Large Grid Display */}
                    <div className="flex justify-center">
                      <div className="grid grid-cols-6 gap-3 w-fit">
                        {/* Headers */}
                        <div className="w-12 h-12"></div>
                        {[1, 2, 3, 4, 5].map(col => (
                          <div key={col} className={`w-12 h-12 flex items-center justify-center text-lg font-bold border-2 ${
                            isDarkMode ? 'bg-indigo-700 border-indigo-600 text-white' : 'bg-indigo-100 border-indigo-300 text-indigo-900'
                          }`}>
                            {coordinateStyle === 'numeric' ? col : String.fromCharCode(64 + col)}
                          </div>
                        ))}
                        
                        {/* Grid with letters */}
                        {polybiusGrid.map((row, rowIndex) => (
                          <React.Fragment key={rowIndex}>
                            <div className={`w-12 h-12 flex items-center justify-center text-lg font-bold border-2 ${
                              isDarkMode ? 'bg-indigo-700 border-indigo-600 text-white' : 'bg-indigo-100 border-indigo-300 text-indigo-900'
                            }`}>
                              {coordinateStyle === 'numeric' ? rowIndex + 1 : String.fromCharCode(65 + rowIndex)}
                            </div>
                            {row.map((letter, colIndex) => (
                              <div
                                key={`${rowIndex}-${colIndex}`}
                                className={`w-12 h-12 flex items-center justify-center text-lg font-mono font-bold border-2 ${
                                  isDarkMode ? 'bg-gray-600 border-gray-500 text-white hover:bg-gray-500' : 'bg-white border-gray-400 text-gray-900 hover:bg-gray-100'
                                } transition-colors cursor-pointer`}
                                title={`Letter: ${letter} → Coordinates: ${coordinatesToString(rowIndex, colIndex)}`}
                              >
                                {letter}
                              </div>
                            ))}
                          </React.Fragment>
                        ))}
                      </div>
                    </div>
                    
                    <div className="text-center space-y-4">
                      <div className="text-sm opacity-60 space-y-2">
                        <div><strong>How to use the grid:</strong></div>
                        <div>• Find your letter in the grid</div>
                        <div>• The coordinates are row-column position</div>
                        <div>• A → {coordinatesToString(0, 0)}, B → {coordinatesToString(0, 1)}, etc.</div>
                        <div>• Each letter becomes a coordinate pair</div>
                      </div>
                      
                      {/* Example */}
                      <div className={`p-4 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                        <div className="text-sm font-medium mb-2">Example:</div>
                        <div className="font-mono text-xs space-y-1">
                          <div><strong>Letter H:</strong> Found at position {coordinatesToString(1, 2)}</div>
                          <div><strong>Letter E:</strong> Found at position {coordinatesToString(1, 0)}</div>
                          <div><strong>Word "HE":</strong> Becomes "{coordinatesToString(1, 2)}{coordinatesToString(1, 0)}"</div>
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Help Modal */}
        {activeModal === 'help' && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg p-6 max-w-2xl w-full max-h-[80vh] overflow-y-auto`}>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold flex items-center space-x-2">
                  <Shield className="text-indigo-500" />
                  <span>About Polybius Square Cipher</span>
                </h2>
                <button
                  onClick={() => setActiveModal(null)}
                  className={`p-2 rounded ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
                >
                  ×
                </button>
              </div>
              
              <div className="space-y-4 text-sm">
                <div>
                  <h3 className="font-semibold mb-2">What is the Polybius Square?</h3>
                  <p>The Polybius Square is one of the oldest known ciphers, dating back to ancient Greece around 200 BC. It was invented by the Greek historian Polybius and represents letters as coordinate pairs using a 5×5 grid. This cipher transforms alphabetic text into numeric coordinates, making it both a substitution cipher and a coordinate-based encoding system.</p>
                </div>
                
                <div>
                  <h3 className="font-semibold mb-2">How it Works</h3>
                  <ol className="list-decimal list-inside space-y-1">
                    <li>Create a 5×5 grid containing the alphabet (25 letters)</li>
                    <li>Label rows and columns with numbers 1-5 or letters A-E</li>
                    <li>Each letter is replaced by its row-column coordinates</li>
                    <li>The letter A (position 1,1) becomes "11" or "AA"</li>
                    <li>Multiple letters form coordinate sequences</li>
                  </ol>
                </div>
                
                <div>
                  <h3 className="font-semibold mb-2">Standard Grid Layout</h3>
                  <div className={`p-3 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                    <div className="text-xs font-mono text-center space-y-1">
                      <div><strong>   1 2 3 4 5</strong></div>
                      <div><strong>1</strong>  A B C D E</div>
                      <div><strong>2</strong>  F G H I K</div>
                      <div><strong>3</strong>  L M N O P</div>
                      <div><strong>4</strong>  Q R S T U</div>
                      <div><strong>5</strong>  V W X Y Z</div>
                      <div className="text-gray-500 mt-2">Note: J is typically omitted or combined with I</div>
                    </div>
                  </div>
                </div>
                
                <div>
                  <h3 className="font-semibold mb-2">Encryption Example</h3>
                  <div className={`p-3 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                    <div className="font-mono text-xs space-y-1">
                      <div><strong>Plaintext:</strong> "HELLO"</div>
                      <div><strong>Process:</strong></div>
                      <div>• H (position 2,3) → 23</div>
                      <div>• E (position 1,5) → 15</div>
                      <div>• L (position 3,1) → 31</div>
                      <div>• L (position 3,1) → 31</div>
                      <div>• O (position 3,4) → 34</div>
                      <div><strong>Ciphertext:</strong> "23 15 31 31 34"</div>
                    </div>
                  </div>
                </div>
                
                <div>
                  <h3 className="font-semibold mb-2">Historical Background</h3>
                  <ul className="space-y-1">
                    <li><strong>Inventor:</strong> Polybius (c. 200-118 BC)</li>
                    <li><strong>Origin:</strong> Ancient Greece</li>
                    <li><strong>Purpose:</strong> Military and diplomatic communication</li>
                    <li><strong>Method:</strong> Visual signaling with torches</li>
                    <li><strong>Innovation:</strong> First coordinate-based cipher system</li>
                    <li><strong>Usage:</strong> Telegraphy and optical communication</li>
                  </ul>
                </div>
                
                <div>
                  <h3 className="font-semibold mb-2">Polybius's Original System</h3>
                  <p>Polybius originally designed this system for long-distance communication using torch signals. Messengers would hold up torches in two groups - the first group indicated the row, the second group indicated the column. This allowed complex messages to be transmitted visually across great distances using hilltop relay stations.</p>
                </div>
                
                <div>
                  <h3 className="font-semibold mb-2">Variations and Improvements</h3>
                  <ul className="space-y-1">
                    <li><strong>Keyword Polybius:</strong> Fill grid with keyword first</li>
                    <li><strong>Mixed Alphabet:</strong> Randomized letter arrangement</li>
                    <li><strong>Nihilist Cipher:</strong> Polybius + keyword addition</li>
                    <li><strong>Bifid Cipher:</strong> Polybius coordinates manipulation</li>
                    <li><strong>Trifid Cipher:</strong> 3D extension with three coordinates</li>
                    <li><strong>ADFGVX:</strong> Military variant with letter coordinates</li>
                  </ul>
                </div>
                
                <div>
                  <h3 className="font-semibold mb-2">Advantages</h3>
                  <ul className="space-y-1">
                    <li>Simple to understand and implement</li>
                    <li>Perfect for visual/optical communication</li>
                    <li>Converts letters to numbers systematically</li>
                    <li>Foundation for more complex ciphers</li>
                    <li>Historically significant and educational</li>
                    <li>Works well with limited character sets</li>
                  </ul>
                </div>
                
                <div>
                  <h3 className="font-semibold mb-2">Weaknesses</h3>
                  <ul className="space-y-1">
                    <li>Simple substitution - vulnerable to frequency analysis</li>
                    <li>Predictable coordinate patterns</li>
                    <li>No key security (standard grid)</li>
                    <li>Easily recognizable numeric pairs</li>
                    <li>Limited alphabet (25 letters)</li>
                    <li>Doubled message length</li>
                  </ul>
                </div>
                
                <div>
                  <h3 className="font-semibold mb-2">Modern Applications</h3>
                  <ul className="space-y-1">
                    <li>Cryptography education and training</li>
                    <li>Historical cipher demonstrations</li>
                    <li>Foundation for learning coordinate systems</li>
                    <li>Puzzle creation and escape rooms</li>
                    <li>Understanding substitution principles</li>
                    <li>Building blocks for advanced ciphers</li>
                  </ul>
                </div>
                
                <div>
                  <h3 className="font-semibold mb-2">Breaking the Cipher</h3>
                  <ul className="space-y-1">
                    <li><strong>Pattern Recognition:</strong> Look for repeated number pairs</li>
                    <li><strong>Frequency Analysis:</strong> Most common pairs likely common letters</li>
                    <li><strong>Word Patterns:</strong> Known word structures</li>
                    <li><strong>Grid Reconstruction:</strong> Deduce the alphabet arrangement</li>
                    <li><strong>Statistical Methods:</strong> Compare to expected letter frequencies</li>
                  </ul>
                </div>
                
                <div>
                  <h3 className="font-semibold mb-2">Educational Value</h3>
                  <p>The Polybius Square is excellent for learning fundamental cryptographic concepts. It demonstrates coordinate systems, substitution principles, and the relationship between plaintext and ciphertext. Students can easily understand how ancient civilizations solved communication challenges using mathematical principles.</p>
                </div>
                
                <div>
                  <h3 className="font-semibold mb-2">Cultural Impact</h3>
                  <ul className="space-y-1">
                    <li>Influenced development of telegraphy</li>
                    <li>Used in prisoner communication systems</li>
                    <li>Foundation for modern coordinate-based systems</li>
                    <li>Inspired optical communication networks</li>
                    <li>Demonstrated mathematical approach to secrecy</li>
                    <li>Bridge between ancient and modern cryptography</li>
                  </ul>
                </div>
                
                <div>
                  <h3 className="font-semibold mb-2">Comparison with Other Ancient Ciphers</h3>
                  <ul className="space-y-1">
                    <li><strong>vs Caesar Cipher:</strong> More complex but also more vulnerable</li>
                    <li><strong>vs Atbash:</strong> More systematic coordinate approach</li>
                    <li><strong>vs Scytale:</strong> Mathematical rather than physical</li>
                    <li><strong>vs Steganography:</strong> Obvious encryption vs hidden messages</li>
                  </ul>
                </div>
                
                <div>
                  <h3 className="font-semibold mb-2">Security Level</h3>
                  <p className="text-orange-600">⚠️ <strong>Educational Use Only:</strong> The Polybius Square provides minimal security by modern standards. It's easily broken with frequency analysis and pattern recognition. However, it remains valuable for understanding cryptographic principles and as a foundation for more sophisticated cipher systems.</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Settings Modal */}
        {activeModal === 'settings' && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg p-6 max-w-md w-full max-h-[80vh] overflow-y-auto`}>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold">Advanced Options</h2>
                <button
                  onClick={() => setActiveModal(null)}
                  className={`p-2 rounded ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
                >
                  ×
                </button>
              </div>
              
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium">Show Grid</label>
                  <input
                    type="checkbox"
                    checked={showGrid}
                    onChange={(e) => setShowGrid(e.target.checked)}
                    className="rounded"
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium">Use Keyword</label>
                  <input
                    type="checkbox"
                    checked={useKeyword}
                    onChange={(e) => setUseKeyword(e.target.checked)}
                    className="rounded"
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium">Group Output</label>
                  <input
                    type="checkbox"
                    checked={groupOutput}
                    onChange={(e) => setGroupOutput(e.target.checked)}
                    className="rounded"
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium">J replaces I</label>
                  <input
                    type="checkbox"
                    checked={jReplacesI}
                    onChange={(e) => setJReplacesI(e.target.checked)}
                    className="rounded"
                  />
                </div>
                
                {groupOutput && (
                  <>
                    <div>
                      <label className="block text-sm font-medium mb-2">Group Size</label>
                      <input
                        type="range"
                        min="2"
                        max="10"
                        value={groupSize}
                        onChange={(e) => setGroupSize(parseInt(e.target.value))}
                        className="w-full"
                      />
                      <div className="text-xs opacity-60 mt-1">
                        Characters per group: {groupSize}
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium mb-2">Separator</label>
                      <select
                        value={separator}
                        onChange={(e) => setSeparator(e.target.value)}
                        className={`w-full p-2 rounded border ${
                          isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'
                        }`}
                      >
                        <option value=" ">Space</option>
                        <option value="-">Dash</option>
                        <option value=".">Dot</option>
                        <option value=",">Comma</option>
                        <option value="|">Pipe</option>
                      </select>
                    </div>
                  </>
                )}
                
                <div className="pt-4 border-t">
                  <h3 className="text-sm font-medium mb-3">Historical Keywords</h3>
                  <div className="grid grid-cols-2 gap-2">
                    {['POLYBIUS', 'SQUARE', 'ANCIENT', 'GREECE', 'CIPHER', 'SECRET', 'CRYPTO', 'MATRIX', 'COORDS', 'SIGNAL', 'TORCH', 'BEACON'].map((word, index) => (
                      <button
                        key={index}
                        onClick={() => setKeyword(word)}
                        className={`p-2 text-xs rounded border ${
                          isDarkMode ? 'bg-gray-700 border-gray-600 hover:bg-gray-600' : 'bg-gray-100 border-gray-300 hover:bg-gray-200'
                        } transition-colors`}
                      >
                        {word}
                      </button>
                    ))}
                  </div>
                </div>
                
                <div className="pt-4 border-t">
                  <h3 className="text-sm font-medium mb-3">Cipher Presets</h3>
                  <div className="space-y-2">
                    <button
                      onClick={() => {
                        setKeyword('POLYBIUS');
                        setUseKeyword(true);
                        setCoordinateStyle('numeric');
                        setGroupOutput(true);
                        setGroupSize(2);
                        setSeparator(' ');
                        setJReplacesI(true);
                        setShowGrid(true);
                      }}
                      className={`w-full p-2 text-sm rounded border ${
                        isDarkMode ? 'bg-indigo-600 border-indigo-500 hover:bg-indigo-700 text-white' : 'bg-indigo-500 border-indigo-400 hover:bg-indigo-600 text-white'
                      } transition-colors`}
                    >
                      Classical Greek
                    </button>
                    
                    <button
                      onClick={() => {
                        setKeyword('');
                        setUseKeyword(false);
                        setCoordinateStyle('numeric');
                        setGroupOutput(false);
                        setJReplacesI(true);
                        setShowGrid(true);
                      }}
                      className={`w-full p-2 text-sm rounded border ${
                        isDarkMode ? 'bg-purple-600 border-purple-500 hover:bg-purple-700 text-white' : 'bg-purple-500 border-purple-400 hover:bg-purple-600 text-white'
                      } transition-colors`}
                    >
                      Standard Grid
                    </button>
                    
                    <button
                      onClick={() => {
                        setKeyword('SECRET');
                        setUseKeyword(true);
                        setCoordinateStyle('alphabetic');
                        setGroupOutput(true);
                        setGroupSize(4);
                        setSeparator('-');
                        setJReplacesI(false);
                        setShowGrid(false);
                      }}
                      className={`w-full p-2 text-sm rounded border ${
                        isDarkMode ? 'bg-green-600 border-green-500 hover:bg-green-700 text-white' : 'bg-green-500 border-green-400 hover:bg-green-600 text-white'
                      } transition-colors`}
                    >
                      Modern Educational
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Analytics Modal */}
        {activeModal === 'analytics' && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg p-6 max-w-3xl w-full max-h-[80vh] overflow-y-auto`}>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold">Analytics Dashboard</h2>
                <button
                  onClick={() => setActiveModal(null)}
                  className={`p-2 rounded ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
                >
                  ×
                </button>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className={`p-4 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                  <div className="text-2xl font-bold text-green-500">{stats.totalEncryptions}</div>
                  <div className="text-sm">Total Encryptions</div>
                </div>
                <div className={`p-4 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                  <div className="text-2xl font-bold text-blue-500">{stats.totalDecryptions}</div>
                  <div className="text-sm">Total Decryptions</div>
                </div>
                <div className={`p-4 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                  <div className="text-2xl font-bold text-purple-500">{stats.coordinatePairsProcessed}</div>
                  <div className="text-sm">Coordinate Pairs</div>
                </div>
                <div className={`p-4 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                  <div className="text-2xl font-bold text-orange-500">{stats.totalCharactersProcessed}</div>
                  <div className="text-sm">Characters Processed</div>
                </div>
                <div className={`p-4 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                  <div className="text-2xl font-bold text-pink-500">{stats.uniqueKeywords.size}</div>
                  <div className="text-sm">Unique Keywords</div>
                </div>
                <div className={`p-4 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                  <div className="text-2xl font-bold text-cyan-500">{stats.averageKeywordLength}</div>
                  <div className="text-sm">Avg Keyword Length</div>
                </div>
                <div className={`p-4 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                  <div className="text-2xl font-bold text-amber-500">{stats.totalGridsUsed}</div>
                  <div className="text-sm">Grids Used</div>
                </div>
                <div className={`p-4 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                  <div className="text-2xl font-bold text-red-500">{stats.longestText}</div>
                  <div className="text-sm">Longest Text</div>
                </div>
              </div>
              
              <div>
                <h3 className="text-lg font-semibold mb-4">Recent Operations</h3>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {history.length > 0 ? history.slice(0, 15).map(item => (
                    <div key={item.id} className={`p-3 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center space-x-2">
                          <span className={`text-xs px-2 py-1 rounded ${
                            item.operation === 'encrypt' ? 'bg-green-500 text-white' : 'bg-blue-500 text-white'
                          }`}>
                            {item.operation.toUpperCase()}
                          </span>
                          <span className="text-xs px-2 py-1 rounded bg-purple-500 text-white">
                            {item.coordinatePairs} coords
                          </span>
                          {item.keyword && (
                            <span className="text-xs px-2 py-1 rounded bg-indigo-500 text-white">
                              {item.keyword}
                            </span>
                          )}
                        </div>
                        <span className="text-xs opacity-60">
                          {item.timestamp.toLocaleTimeString()}
                        </span>
                      </div>
                      <div className="text-sm">
                        <div className="truncate font-mono">{item.input}</div>
                        <div className="truncate opacity-60 font-mono">→ {item.output}</div>
                      </div>
                    </div>
                  )) : (
                    <div className="text-center py-8 opacity-60">
                      <Hash size={32} className="mx-auto mb-2" />
                      <div>No operations performed yet</div>
                      <div className="text-xs">Start encrypting or decrypting to see analytics</div>
                    </div>
                  )}
                </div>
              </div>
              
              {history.length > 0 && (
                <div className="mt-6 pt-4 border-t">
                  <h3 className="text-sm font-medium mb-2">Cipher Statistics</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs">
                    <div>
                      <div className="text-purple-500">Most Used Keyword:</div>
                      <div>
                        {history.length > 0 
                          ? (() => {
                              const keywordCounts = history.reduce((acc, item) => {
                                if (item.keyword) {
                                  acc[item.keyword] = (acc[item.keyword] || 0) + 1;
                                }
                                return acc;
                              }, {} as Record<string, number>);
                              const mostUsed = Object.keys(keywordCounts).length > 0 
                                ? Object.keys(keywordCounts).reduce((a, b) => keywordCounts[a] > keywordCounts[b] ? a : b)
                                : 'None';
                              return mostUsed;
                            })()
                          : 'N/A'
                        }
                      </div>
                    </div>
                    <div>
                      <div className="text-green-500">Encrypt/Decrypt Ratio:</div>
                      <div>{stats.totalOperations > 0 ? Math.round((stats.totalEncryptions / stats.totalOperations) * 100) : 0}% Encrypt</div>
                    </div>
                    <div>
                      <div className="text-blue-500">Avg Coords per Op:</div>
                      <div>{stats.totalOperations > 0 ? Math.round(stats.coordinatePairsProcessed / stats.totalOperations) : 0}</div>
                    </div>
                    <div>
                      <div className="text-orange-500">Grid Efficiency:</div>
                      <div>{stats.totalCharactersProcessed > 0 ? Math.round((stats.coordinatePairsProcessed / stats.totalCharactersProcessed) * 100) : 0}% Mapped</div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default PolybiusSquareCipherApp;